/**
 * The auth + retry policy shared by every ai-engine request: refresh a stale
 * token before the call goes out, and retry exactly once on a 401 after a
 * fresh refresh. Anything else — including a second 401 — is surfaced as-is.
 */

/** Structural auth port. `MedplumClient` already satisfies this shape. */
export interface ChatAuth {
  getAccessToken(): string | undefined;
  refreshIfExpired(): Promise<unknown>;
}

export type ChatFetch = (path: string, init?: RequestInit) => Promise<Response>;

/** Builds a fetch scoped to `baseUrl`, carrying `auth`'s bearer token. */
export function makeChatFetch(auth: ChatAuth, baseUrl: string): ChatFetch {
  async function chatFetch(path: string, init?: RequestInit, isRetry = false): Promise<Response> {
    await auth.refreshIfExpired();
    const token = auth.getAccessToken();

    const response = await fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        ...init?.headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (response.status === 401 && !isRetry) {
      await auth.refreshIfExpired();
      return chatFetch(path, init, true);
    }

    return response;
  }

  return chatFetch;
}
